import { getNow } from "@sg-protect/core";
import type { ComponentsTFunction, ComponentsTranslationKey } from "../i18n/i18next.ts";

const DAY_MS = 24 * 60 * 60 * 1000;

export { getNow };

export function daysFromNow(days: number): string {
    return new Date(getNow() + days * DAY_MS).toISOString();
}

const NO_LINK_EXPIRATION_TIME = Date.parse("0001-01-01T00:00:00Z");

export function hasLinkExpiration(expirationDate: string | null | undefined): expirationDate is string {
    return expirationDate != null && Date.parse(expirationDate) !== NO_LINK_EXPIRATION_TIME;
}

export function isLinkExpired(expirationDate: string | null | undefined): boolean {
    return hasLinkExpiration(expirationDate) && Date.parse(expirationDate) < getNow();
}

export function differenceInDays(end: number | string | Date, start: number | string | Date): number {
    return Math.floor((new Date(end).getTime() - new Date(start).getTime()) / DAY_MS);
}

const DAY_THRESHOLDS = [
    { limit: 30, divisor: 1, unit: "day" },
    { limit: 365, divisor: 30, unit: "month" },
    { limit: Infinity, divisor: 365, unit: "year" }
] as const;

export type DaysCountUnit = (typeof DAY_THRESHOLDS)[number]["unit"];

const DAYS_COUNT_KEYS = {
    day: "DaysCount.day",
    month: "DaysCount.month",
    year: "DaysCount.year"
} as const satisfies Record<DaysCountUnit, ComponentsTranslationKey>;

export function toDaysCountUnit(days: number) {
    const { divisor, unit } = DAY_THRESHOLDS.find(({ limit }) => days < limit)!;

    return { count: Math.floor(days / divisor), unit };
}

// "3 days", "2 months", "1 year"
export function formatDaysCount(days: number, t: ComponentsTFunction): string {
    const { count, unit } = toDaysCountUnit(days);

    return t(DAYS_COUNT_KEYS[unit], { count });
}

// "today", "3 days ago", "2 months ago"
export function formatDaysAgo(date: string | Date | number, locale: string): string {
    const days = differenceInDays(getNow(), date);

    if (days <= 0) {
        return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(0, "day");
    }

    const { count, unit } = toDaysCountUnit(days);

    return new Intl.RelativeTimeFormat(locale, { numeric: "always" }).format(-count, unit);
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

// Past this age, formatRelativeTime switches from "X minutes ago" to an absolute time (e.g. "Today at 2:34 PM"),
// and useRelativeTime stops re-rendering since the absolute label no longer changes minute to minute.
const RELATIVE_TIME_CUTOFF_MS = HOUR_MS;

function isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatAbsoluteTime(date: Date, now: Date, locale: string, t: ComponentsTFunction): string {
    const timeFormatter = new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" });
    const dayFormatter = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" });

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    const time = timeFormatter.format(date);

    if (isSameDay(date, now)) {
        return t("RelativeTime.today", { time });
    }

    if (isSameDay(date, yesterday)) {
        return t("RelativeTime.yesterday", { time });
    }

    return t("RelativeTime.otherDay", { date: dayFormatter.format(date), time });
}

// Past this point formatRelativeTime shows an absolute time instead of a relative one, so re-rendering on a tick is pointless.
export function isPastRelativeTimeCutoff(date: string | Date | number): boolean {
    return getNow() - new Date(date).getTime() >= RELATIVE_TIME_CUTOFF_MS;
}

// "just now", "2 minutes ago", "Today at 2:34 PM", "Yesterday at 9:15 AM", "Jan 5 at 9:15 AM"
export function formatRelativeTime(date: string | Date | number, locale: string, t: ComponentsTFunction): string {
    const targetDate = new Date(date);
    const nowDate = new Date(getNow());
    const elapsedMs = Math.max(nowDate.getTime() - targetDate.getTime(), 0);

    if (elapsedMs < MINUTE_MS) {
        return t("RelativeTime.justNow");
    }

    if (!isPastRelativeTimeCutoff(date)) {
        const relativeTimeFormatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

        return relativeTimeFormatter.format(-Math.floor(elapsedMs / MINUTE_MS), "minute");
    }

    return formatAbsoluteTime(targetDate, nowDate, locale, t);
}

const ELAPSED_TIME_UNITS = [
    { limit: HOUR_MS, divisor: MINUTE_MS, unit: "minute" },
    { limit: DAY_MS, divisor: HOUR_MS, unit: "hour" },
    { limit: Infinity, divisor: DAY_MS, unit: "day" }
] as const;

// "just now", "2 minutes ago", "4 hours ago", "3 days ago"
export function formatElapsedTime(date: string | Date | number, locale: string, t: ComponentsTFunction): string {
    const elapsedMs = Math.max(getNow() - new Date(date).getTime(), 0);

    if (elapsedMs < MINUTE_MS) {
        return t("RelativeTime.justNow");
    }

    const { divisor, unit } = ELAPSED_TIME_UNITS.find(({ limit }) => elapsedMs < limit)!;
    const formatter = new Intl.RelativeTimeFormat(locale);

    return formatter.format(-Math.floor(elapsedMs / divisor), unit);
}
